import {

  createContext,

  useCallback,

  useContext,

  useEffect,

  useMemo,

  useState,

  type ReactNode,

} from "react";

import type { Session, User } from "@supabase/supabase-js";

import { flushPendingSignupConsentIfNeeded } from "@/api/privacy";

import { supabase, supabaseConfigured } from "@/lib/supabase";



interface AuthContextValue {

  configured: boolean;

  loading: boolean;

  session: Session | null;

  user: User | null;

  accessToken: string | null;

  signIn: (email: string, password: string) => Promise<void>;

  signUp: (email: string, password: string) => Promise<{ needsEmailConfirmation: boolean }>;

  resetPassword: (email: string) => Promise<void>;

  updatePassword: (password: string) => Promise<void>;

  signOut: () => Promise<void>;

}



const AuthContext = createContext<AuthContextValue | null>(null);



export function AuthProvider({ children }: { children: ReactNode }) {

  const [loading, setLoading] = useState(supabaseConfigured);
  const [session, setSession] = useState<Session | null>(null);



  useEffect(() => {

    if (!supabaseConfigured) {

      setLoading(false);

      return;

    }



    supabase.auth.getSession().then(({ data }) => {

      setSession(data.session);

      setLoading(false);

      if (data.session) {

        void flushPendingSignupConsentIfNeeded();

      }

    });



    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {

      setSession(next);

      setLoading(false);

      if (next && (event === "SIGNED_IN" || event === "PASSWORD_RECOVERY")) {

        void flushPendingSignupConsentIfNeeded();

      }

    });



    return () => sub.subscription.unsubscribe();

  }, []);



  const signIn = useCallback(async (email: string, password: string) => {

    const { error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) throw error;

  }, []);



  const signUp = useCallback(async (email: string, password: string) => {

    const { data, error } = await supabase.auth.signUp({ email, password });

    if (error) throw error;

    return { needsEmailConfirmation: !data.session };

  }, []);



  const resetPassword = useCallback(async (email: string) => {

    const redirectTo = `${window.location.origin}/reset-password`;

    const { error } = await supabase.auth.resetPasswordForEmail(email, {

      redirectTo,

    });

    if (error) throw error;

  }, []);



  const updatePassword = useCallback(async (password: string) => {

    const { error } = await supabase.auth.updateUser({ password });

    if (error) throw error;

    await supabase.auth.signOut();

  }, []);



  const signOut = useCallback(async () => {

    await supabase.auth.signOut();

  }, []);



  const value = useMemo<AuthContextValue>(

    () => ({

      configured: supabaseConfigured,

      loading,

      session,

      user: session?.user ?? null,

      accessToken: session?.access_token ?? null,

      signIn,

      signUp,

      resetPassword,

      updatePassword,

      signOut,

    }),

    [loading, session, signIn, signUp, resetPassword, updatePassword, signOut],

  );



  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;

}



export function useAuth(): AuthContextValue {

  const ctx = useContext(AuthContext);

  if (!ctx) throw new Error("useAuth must be used within AuthProvider");

  return ctx;

}

